<template>
  <component
    :is="hasClick ? 'button' : 'div'"
    :disabled="disabled"
    :class="[borderClass, paddingClass, textClass, backgroundClass]"
    class="rounded-small group relative flex h-8 shrink-0 flex-row items-center justify-center overflow-hidden whitespace-nowrap select-none"
  >
    <component
      v-if="icon && (type != 'filter' || !selected)"
      :class="[
        iconCallback && 'hover:bg-surface-container-highest cursor-pointer rounded-full p-0.5',
        'mr-2 grid place-items-center',
      ]"
      :is="iconCallback ? 'button' : 'div'"
      @click.stop="
        () => {
          if (iconCallback) {
            iconCallback()
          }
        }
      "
    >
      <BaseIcon
        size="18"
        :filled="iconFilled"
        :class="[
          !disabled && 'text-primary',
          (type != 'filter' || !selected) && disabled && 'text-on-surface/40',
        ]"
        >{{ icon }}
      </BaseIcon>
    </component>
    <img
      v-if="image && (type != 'filter' || !selected)"
      :src="image"
      class="text-primary mr-2 h-[18px] w-[18px] rounded-full"
    />
    <BaseIcon v-if="type == 'filter' && selected" size="18" class="mr-2" :class="[textClass]"
      >check</BaseIcon
    >
    <BaseMemberAvatar v-if="member" :member="member" size="24" class="mr-2" omit-tooltip />
    <span class="text-label-large whitespace-nowrap">
      <slot />
    </span>
    <BaseIcon v-if="menu != undefined" size="18" class="-mr-2 ml-2">{{
      menu == 'close' ? 'arrow_drop_down' : 'arrow_drop_up'
    }}</BaseIcon>
    <BaseIcon
      v-if="type == 'input'"
      size="18"
      class="ml-2"
      :class="[disabled && 'text-on-surface/40', textClass]"
      >close</BaseIcon
    >
    <BaseStateLayer v-if="hasClick" />
  </component>
</template>
<script lang="ts" setup>
  import { computed, useAttrs } from 'vue'
  import BaseIcon from '../icon/BaseIcon.vue'
  import type { BaseChipType } from './chip-type'
  import BaseStateLayer from '../state-layer/BaseStateLayer.vue'
  import type { Member } from '../../../models/member/member'
  import BaseMemberAvatar from '../avatar/BaseMemberAvatar.vue'

  interface Props {
    type?: BaseChipType
    icon?: string
    image?: string
    selected?: boolean
    iconFilled?: boolean
    disabled?: boolean
    menu?: 'close' | 'open'
    member?: Member
    iconCallback?: () => void
    color?: string
  }
  const {
    type = 'assist',
    disabled,
    selected,
    icon,
    image,
    menu,
    member,
    color,
  } = defineProps<Props>()

  const attrs = useAttrs()

  const hasClick = computed<boolean>(() => {
    return attrs.onClick != undefined || menu != undefined
  })

  const borderClass = computed(() => {
    if (disabled) {
      return 'border border-on-surface/12'
    }
    if (color) {
      return `border border-${color}-50`
    }
    if (type == 'filter' && !selected) {
      return 'border border-outline-variant'
    }

    return 'border border-outline-variant'
  })

  const paddingClass = computed(() => {
    if (icon || image || member) {
      if (type == 'input' || menu != undefined) {
        return 'px-2'
      } else {
        return 'pl-2 pr-3'
      }
    } else {
      if (type == 'input' || menu != undefined) {
        return 'pl-3 pr-2'
      } else {
        return 'px-3'
      }
    }
  })

  const textClass = computed(() => {
    if (disabled) {
      return 'text-on-surface/38'
    }

    if (color && (type != 'filter' || selected)) {
      return `text-${color}-40 dark:text-${color}-90`
    }
    if (color && type === 'filter' && !selected) {
      return `text-${color}-60 dark:text-${color}-20`
    }

    if (type == 'filter' && selected) {
      return 'text-on-secondary-container'
    }

    return 'text-on-surface'
  })

  const backgroundClass = computed(() => {
    if (color && (type != 'filter' || selected)) {
      return `bg-${color}-95 dark:bg-${color}-30`
    }

    if (type == 'filter' && selected) {
      return 'bg-secondary-container'
    }
  })
</script>
